<template>
    <Dialog
        :visible="visible"
        modal
        dismissableMask
        :closable="false"
        :draggable="false"
        :style="{ width: '60rem', maxWidth: '95vw' }"
        contentClass="!p-0 overflow-hidden rounded-lg"
        :pt="{ header: { class: '!p-0' } }"
        @update:visible="(v) => !v && handleClose()"
    >
        <!-- HEADER -->
        <template #header>
            <div :class="['flex items-center gap-4 w-full p-3 rounded-t-lg', isDelete ? 'bg-gradient-to-r from-red-50 to-rose-50' : mode === 'create' ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-blue-50 to-indigo-50']">
                <div :class="['w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0', isDelete ? 'bg-red-500' : mode === 'create' ? 'bg-green-500' : 'bg-blue-500']">
                    <i :class="['text-white text-xl', isDelete ? 'fa-regular fa-trash-can' : mode === 'create' ? 'fa-solid fa-plus' : 'fa-regular fa-pen-to-square']" />
                </div>

                <div class="flex-1 min-w-0">
                    <h5 class="font-bold">
                        {{ dialogTitle }}
                    </h5>
                </div>

                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')" @click="handleClose()" />
            </div>
        </template>

        <!-- BODY -->
        <form @submit.prevent="save">
            <!-- TABS -->
            <div v-if="!isDelete" class="border-b border-surface-200 px-6 pt-1 overflow-x-auto">
                <div class="flex gap-1 min-w-max">
                    <button v-for="tab in tabs" :key="tab.value" type="button" @click="activeTab = tab.value" :class="['px-4 py-3 font-medium relative shrink-0 whitespace-nowrap', activeTab === tab.value ? 'text-primary' : 'hover:text-primary']">
                        <div class="flex items-center gap-2">
                            <i :class="tab.icon" />
                            <span>{{ tab.label }}</span>
                            <Tag v-if="tab.count !== undefined" :value="tab.count" severity="info" />
                        </div>
                        <div v-if="activeTab === tab.value" class="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                    </button>
                </div>
            </div>

            <!-- CONTENT -->
            <div class="p-4 max-h-[60vh] overflow-y-auto">
                <!-- GENERAL -->
                <div v-show="activeTab === 'general' || isDelete" class="space-y-6">
                    <div>
                        <label class="font-semibold"> {{ t('common.name') }} <span class="text-red-500">*</span> </label>
                        <InputText v-model="form.name" :disabled="isDelete" class="w-full" :invalid="validation.name.invalid" @blur="touched.name = true" />
                        <small v-if="validation.name.invalid" class="text-red-500">
                            {{ validation.name.message }}
                        </small>
                    </div>

                    <div>
                        <label class="font-semibold">
                            {{ t('common.description') }}
                        </label>
                        <Textarea v-model="form.description" rows="4" :disabled="isDelete" class="w-full" />
                    </div>

                    <div>
                        <label class="font-semibold"> {{ t('common.status') }} <span class="text-red-500">*</span> </label>
                        <Select
                            v-model="form.enabled"
                            :options="[
                                { label: t('common.enabled'), value: true },
                                { label: t('common.disabled'), value: false }
                            ]"
                            optionLabel="label"
                            optionValue="value"
                            :disabled="isDelete"
                            class="w-full"
                            :invalid="validation.enabled.invalid"
                            @change="touched.enabled = true"
                            @blur="touched.enabled = true"
                        />
                        <small v-if="validation.enabled.invalid" class="text-red-500">
                            {{ validation.enabled.message }}
                        </small>
                    </div>
                </div>

                <!-- USERS -->
                <div v-show="activeTab === 'users'" class="space-y-4">
                    <InputText v-model="searchQuery" :placeholder="t('userGroups.searchUsersPlaceholder')" class="w-full" />

                    <div v-for="user in filteredUsers" :key="user.id" class="flex items-center justify-between border-b p-2">
                        <div class="flex items-center gap-3">
                            <Checkbox :modelValue="isUserSelected(user.id)" binary @update:modelValue="toggleUser(user.id)" />
                            <div>
                                <h5 class="font-semibold">{{ user.firstName }} {{ user.lastName }}</h5>
                                <p>
                                    {{ user.email }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- NOTIFICATIONS -->
                <div v-show="activeTab === 'notifications' && canManageRecipients" class="space-y-5">
                    <MultiSelect v-model="selectedResourceTypes" :options="availableResourceTypes" optionLabel="name"
                        class="w-full" display="chip" @change="onResourceTypesSelected"
                        :placeholder="t('userGroups.selectResourceTypes')">
                        <template #chip="slotProps">
                            <Tag :value="slotProps.value.name" severity="info" class="mr-1" />
                        </template>
                    </MultiSelect>

                    <div v-for="[id, item] in notifications" :key="id" class="border-2 rounded-xl p-3">
                        <div class="flex justify-between items-center mb-3">
                            <h5 class="font-bold">
                                {{ item.resourceType.name }}
                            </h5>
                            <div class="flex gap-2">
                                <Button v-if="item.enabled"
                                    :icon="allNotificationsEnabled(item) ? 'fa-solid fa-bell-slash' : 'fa-solid fa-bell'"
                                    :label="allNotificationsEnabled(item) ? t('userGroups.disabledAllNotifcations') : t('userGroups.enableAllNotifications')"
                                    :severity="allNotificationsEnabled(item) ? 'secondary' : 'info'" outlined
                                    @click="toggleAllNotifications(item)" />
                                <Button outlined :label="item.enabled ? t('common.disable') : t('common.enable')"
                                    @click="item.enabled = !item.enabled" />
                                <Button icon="fa-regular fa-trash-can" severity="danger" outlined
                                    @click="removeResourceType(item.resourceType.id)" />
                            </div>
                        </div>

                        <div v-if="item.enabled" class="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Button :label="t('userGroups.onCreate')" :severity="item.notifyOnCreate ? 'success' : 'secondary'" @click="item.notifyOnCreate = !item.notifyOnCreate" />
                            <Button :label="t('userGroups.onUpdate')" :severity="item.notifyOnUpdate ? 'info' : 'secondary'" @click="item.notifyOnUpdate = !item.notifyOnUpdate" />
                            <Button :label="t('userGroups.onDelete')" :severity="item.notifyOnDelete ? 'danger' : 'secondary'" @click="item.notifyOnDelete = !item.notifyOnDelete" />
                            <Button :label="t('userGroups.onUnconfirmed')" :severity="item.notifyOnUnconfirmed ? 'warn' : 'secondary'" @click="item.notifyOnUnconfirmed = !item.notifyOnUnconfirmed" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- FOOTER -->
            <div class="flex justify-end p-3 border-t bg-gray-50">
                <Button type="submit"
                    :label="isDelete ? t('common.delete') : mode === 'create' ? t('common.create') : t('common.save')"
                    severity="success" :loading="loading" :disabled="!isDelete && !isFormValid" />
            </div>
        </form>
    </Dialog>
</template>

<script setup lang="ts">
import { ModalMode } from '@/composables/useModal';
import { ResourceType } from '@/lib/types/resourceType';
import { User } from '@/lib/types/user';
import { UserGroup, UserGroupNotification } from '@/lib/types/userGroup';
import { useAuthStore } from '@/store/auth';
import { useResourcesStore } from '@/store/resource';
import { useUsersStore } from '@/store/user';
import { useUserGroupsStore } from '@/store/userGroup';
import { useToast } from 'primevue';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const authStore = useAuthStore();
const toast = useToast();

interface Props {
    visible: boolean;
    mode: ModalMode;
    data: UserGroup | null;
}

const props = defineProps<Props>();
const emit = defineEmits(['close', 'saved']);

const { t } = useI18n();
const store = useUserGroupsStore();
const usersStore = useUsersStore();
const resourcesStore = useResourcesStore();

const activeTab = ref('general');

const form = reactive<UserGroup>({
    id: '',
    name: '',
    description: '',
    users: [],
    enabled: true
});

const errors = reactive({
    name: ''
});

const touched = reactive({
    name: false,
    enabled: false
});

const validation = computed(() => ({
    name: {
        invalid: touched.name && !form.name.trim(),
        message: t('userGroups.validation.nameRequired')
    },
    enabled: {
        invalid: touched.enabled && form.enabled === null,
        message: t('userGroups.validation.statusRequired')
    }
}));

const isFormValid = computed(() => {
    return form.name.trim() !== '' && form.enabled !== null;
});

function touchAllFields() {
    touched.name = true;
    touched.enabled = true;
}

function resetTouched() {
    touched.name = false;
    touched.enabled = false;
}

const searchQuery = ref('');
const selectedUsers = ref<User[]>([]);
const selectedResourceTypes = ref<ResourceType[]>([]);

const notifications = ref<Map<string, UserGroupNotification>>(new Map());

const loading = computed(() => store.loading);
const isDelete = computed(() => props.mode === 'delete');

const dialogTitle = computed(() => (props.mode === 'create' ? t('userGroups.newGroup') : props.mode === 'edit' ? t('userGroups.editGroup') : t('userGroups.deleteGroup')));

const canManageRecipients = computed(() => authStore.hasAuthorization('ManageResourcesRecipients'));

const tabs = computed(() => {
    const list = [
        { value: 'general', label: t('userGroups.tabs.general'), icon: 'fa-solid fa-gear' },
        {
            value: 'users',
            label: t('userGroups.tabs.users'),
            icon: 'fa-solid fa-users',
            count: selectedUsers.value.length
        },
    ];
    if (canManageRecipients.value) {
        list.push({
            value: 'notifications',
            label: t('userGroups.tabs.notifications'),
            icon: 'fa-regular fa-bell',
            count: notifications.value.size
        });
    }
    return list;
});

const filteredUsers = computed(() => {
    const q = searchQuery.value.toLowerCase().trim();
    if (!q) return usersStore.users;

    return usersStore.users.filter((u) => u.firstName.toLowerCase().includes(q) || u.lastName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
});

const availableResourceTypes = computed(() => {
    const usedIds = new Set(notifications.value.keys());
    return resourcesStore.types.filter((rt) => !usedIds.has(rt.id));
});

watch(
    () => [props.visible, props.data] as [boolean, UserGroup | null],
    async ([visible, data]) => {
        if (!visible) return;

        resetForm();
        resetTouched();
        activeTab.value = 'general';

        if ((props.mode === 'edit' || props.mode === 'delete') && data) {
            form.id = data.id;
            form.name = data.name;
            form.description = data.description;
            form.users = [...data.users];
            form.enabled = data.enabled;

            selectedUsers.value = [...data.users];

            if (props.mode === 'edit') {
                await loadNotifications();
            }
        }

        await usersStore.fetchPagedData(1, 1000);
    },
    { immediate: true }
);

async function loadNotifications() {
    if (!props.data) return;

    notifications.value = new Map(Object.entries(store.userGroupNotifications[props.data.id] || {}));

    selectedResourceTypes.value = Array.from(notifications.value.values()).map((n) => n.resourceType);
}

function onResourceTypesSelected(event: any) {
    const selected = event.value as ResourceType[];
    const ids = new Set(selected.map((t) => t.id));

    selected.forEach((type) => {
        if (!notifications.value.has(type.id)) {
            notifications.value.set(type.id, {
                resourceType: type,
                enabled: true,
                notifyOnCreate: false,
                notifyOnUpdate: false,
                notifyOnDelete: false,
                notifyOnUnconfirmed: false
            });
        }
    });

    Array.from(notifications.value.keys()).forEach((id) => {
        if (!ids.has(id)) notifications.value.delete(id);
    });
}

function allNotificationsEnabled(item: UserGroupNotification) {
    return item.notifyOnCreate && item.notifyOnUpdate && item.notifyOnDelete && item.notifyOnUnconfirmed;
}

function toggleAllNotifications(item: UserGroupNotification) {
    const enable = !allNotificationsEnabled(item);
    item.notifyOnCreate = enable;
    item.notifyOnUpdate = enable;
    item.notifyOnDelete = enable;
    item.notifyOnUnconfirmed = enable;
}

function removeResourceType(resourceTypeId: string) {
    notifications.value.delete(resourceTypeId);
    selectedResourceTypes.value = selectedResourceTypes.value.filter((rt) => rt.id !== resourceTypeId);
}

function isUserSelected(id: string) {
    return selectedUsers.value.some((u) => u.id === id);
}

function toggleUser(id: string) {
    const idx = selectedUsers.value.findIndex((u) => u.id === id);
    if (idx >= 0) selectedUsers.value.splice(idx, 1);
    else selectedUsers.value.push(filteredUsers.value.find((u) => u.id === id)!);
}

function resetForm() {
    form.id = '';
    form.name = '';
    form.description = '';
    form.users = [];
    form.enabled = true;

    errors.name = '';
    searchQuery.value = '';
    selectedUsers.value = [];
    selectedResourceTypes.value = [];
    notifications.value.clear();
}

function validate() {
    if (isDelete.value) return true;

    errors.name = '';
    if (!form.name.trim()) {
        errors.name = t('userGroups.nameRequired');
        activeTab.value = 'general';
        return false;
    }
    return true;
}

async function save() {
    touchAllFields();
    if (!validate()) return;

    try {
        if (isDelete.value && props.data) {
            await store.deleteGroup(props.data.id);
            emit('saved');
            handleClose();
            return;
        }

        form.users = selectedUsers.value;

        let groupId: string;

        if (props.mode === 'create') {
            const created = await store.createGroup(form);
            groupId = created.id;
        } else if (props.mode === 'edit' && props.data) {
            await store.updateGroup(form);
            groupId = form.id;
        } else return;

        const list = Array.from(notifications.value.values());
        if (list.length && canManageRecipients.value) {
            await store.saveUserGroupNotifications(groupId, list);
        }

        emit('saved');
        handleClose();
    } catch (err) {
        console.error('Save failed:', err);
        showSaveError(err);
    }
}

function showSaveError(err: unknown) {
    let detail = t('errors.generic');

    if (typeof err === 'object' && err !== null) {
        const e = err as any;
        if (e.response?.status) {
            detail = t('errors.http', { status: e.response.status });
        } else if (e.message) {
            detail = e.message;
        }
    }

    toast.add({
        severity: 'error',
        summary: t('errors.saveFailed'),
        detail,
        life: 5000
    });
}

function handleClose() {
    emit('close');
}
</script>
